/**
 * Admin impersonation state.
 *
 * When an admin starts impersonating a user, the target id is stored
 * in sessionStorage and attached as the ``X-Impersonate-User`` header
 * on every API call (see ``api.ts``). The backend swaps the request
 * identity so every endpoint returns the target user's data.
 *
 * sessionStorage (not localStorage) is intentional: the session ends
 * when the tab closes, so impersonation never silently outlives a
 * support call.
 */

const STORAGE_KEY = "carmen-impersonating";

export type ImpersonationTarget = {
  user_id: string;
  email: string;
  full_name: string;
  access_role: string;
  organization_name: string | null;
};

type Listener = () => void;
const listeners = new Set<Listener>();

export function getImpersonation(): ImpersonationTarget | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ImpersonationTarget;
  } catch {
    return null;
  }
}

export function setImpersonation(target: ImpersonationTarget): void {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  notify();
}

export function clearImpersonation(): void {
  window.sessionStorage.removeItem(STORAGE_KEY);
  notify();
}

export function onImpersonationChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notify(): void {
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* ignore listener errors */
    }
  }
}
